import { useMemo } from 'react';

import { useSession } from '@/context/SessionContext';
import { useTenantFeatureMap } from './useTenantFeatureMap';

const STATUS_FEATURE_KEY = 'status.inventory.enabled';
const GLOBAL_STATUS_PERMISSIONS = ['platform.manage'];
const TENANT_STATUS_PERMISSIONS = ['tenant.status.manage', 'tenant.status.inventory.manage'];

const normalize = (value: string) => value.trim().toLowerCase();

export interface StatusInventoryAccessState {
  featureTenantId?: string;
  statusFeatureEnabled: boolean;
  statusPermissionGranted: boolean;
  accessEnabled: boolean;
}

export function useStatusInventoryAccess(): StatusInventoryAccessState {
  const { session } = useSession();
  const { featureMap, featureTenantId } = useTenantFeatureMap();

  return useMemo(() => {
    const statusFeatureEnabled = featureMap?.[STATUS_FEATURE_KEY] ?? true;
    const permissionSummary = session?.permissionSummary;
    const scopes = session?.scopes ?? [];

    const hasGlobalPermission = permissionSummary?.global?.some(perm =>
      GLOBAL_STATUS_PERMISSIONS.some(target => normalize(target) === normalize(perm))
    );

    const hasTenantPermission = featureTenantId
      ? permissionSummary?.tenants?.[featureTenantId]?.some(perm =>
          TENANT_STATUS_PERMISSIONS.some(target => normalize(target) === normalize(perm))
        )
      : false;

    const accessFromScopes = scopes.some(scope => {
      const normalized = normalize(scope);
      return (
        TENANT_STATUS_PERMISSIONS.some(target => normalized === normalize(target)) ||
        GLOBAL_STATUS_PERMISSIONS.some(target => normalized === normalize(target))
      );
    });

    const statusPermissionGranted = Boolean(hasGlobalPermission || hasTenantPermission || accessFromScopes);

    return {
      featureTenantId,
      statusFeatureEnabled,
      statusPermissionGranted,
      accessEnabled: statusFeatureEnabled && statusPermissionGranted
    };
  }, [featureMap, featureTenantId, session?.permissionSummary, session?.scopes]);
}
